import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Search,
    Plus,
    Edit2,
    History,
    ChevronDown,
    ChevronUp,
    UserPlus,
    Save,
    Check,
    CircleMinus,
    CircleCheck,
    Lock
} from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../../components/common/Toast';
import { confirmWarning, confirmActive } from '../../components/common/confirm';
import organizationPositionsService, {
    Position,
    PositionCreate,
    PositionStats
} from '../../services/organizationPositionsService';
import { authorityApi } from '../../services/authorityApi';
import organizationLocationsService from '../../services/organizationLocationsService';
import postingService, { Posting } from '../../services/postingService';
import apiClient from '../../services/apiService';
import './OrganizationPositions.css';
import Skeleton from '../common/Skeleton';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import AuditHistoryModal, { AuditRecord } from '../../components/common/AuditHistoryModal';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import { activeBadgeCss } from '../../utils/statusVariant';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const RBAC_ACTIVITY = 'ORG_ORGANOGRAM';

interface FillFormData {
    position_id: number; user_id: number; start_date: string;
    end_date: string | null; reference_text: string; posting_type: string;
}
interface UserOption { id: number; name: string; email: string; }
const POSTING_TYPES = ['Direct', 'Promotion', 'Rotation', 'Vacancy'];

type SectionKey = 'list' | 'authority' | 'geography';

const SECTIONS: { key: SectionKey; label: string }[] = [
    { key: 'list', label: 'List' },
    { key: 'authority', label: 'Authority' },
    { key: 'geography', label: 'Geography' },
];

const OrganizationPositions: React.FC = () => {
    const viewPerm    = usePermission(RBAC_ACTIVITY, 'view');
    const canView     = viewPerm.allowed;
    const modifyPerm  = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canModify   = modifyPerm.allowed;
    const permLoading = modifyPerm.loading;

    const { formatDate } = useEntitySettings();

    const [workflowOpenFor, setWorkflowOpenFor] = useState<Position | null>(null);

    const positionVh = useFieldValidation();
    const fillVh = useFieldValidation();

    const [allPositions, setAllPositions] = useState<Position[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [stats, setStats] = useState<PositionStats | null>(null);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('active');
    const [authorityFilter, setAuthorityFilter] = useState<string>('all');
    const [locationFilter, setLocationFilter] = useState<string>('all');

    const [activeSection, setActiveSection] = useState<SectionKey>('list');

    useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, authorityFilter, locationFilter, pageSize, activeSection]);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
    const [isViewMode, setIsViewMode] = useState<boolean>(false);

    const [authorities, setAuthorities] = useState<any[]>([]);
    const [locations, setLocations] = useState<{ id: number; name: string }[]>([]);

    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [historyEntityId, setHistoryEntityId] = useState<number | null>(null);

    const [formData, setFormData] = useState<PositionCreate>({
        name: '',
        code: '',
        description: '',
        is_active: 1,
        authority_id: undefined,
        location_id: undefined
    });

    const [modalError, setModalError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [expandedPositionId, setExpandedPositionId] = useState<number | null>(null);
    const [postingHistory, setPostingHistory] = useState<Posting[]>([]);
    const [postingHistoryLoading, setPostingHistoryLoading] = useState(false);

    const [fillModalOpen, setFillModalOpen] = useState(false);
    const [fillPosition, setFillPosition] = useState<Position | null>(null);
    const [fillPostings, setFillPostings] = useState<Posting[]>([]);
    const [fillPostingsLoading, setFillPostingsLoading] = useState(false);
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [fillFormData, setFillFormData] = useState<FillFormData>({
        position_id: 0, user_id: 0,
        start_date: new Date().toISOString().split('T')[0],
        end_date: null, reference_text: '', posting_type: 'Direct'
    });
    const [fillSaving, setFillSaving] = useState(false);
    const [fillError, setFillError] = useState<string | null>(null);

    // Approved state — determines if form is locked
    const isApproved = currentPosition?.workflow_state === 'approved';
    const fieldsDisabled = !!currentPosition && (!canModify || isApproved || isViewMode);

    // Fill Position modal is locked the same way — an approved position can't
    // have its occupant changed until it's reopened via the workflow button.
    const fillLocked = (fillPosition as any)?.workflow_state === 'approved';

    const fetchStats = useCallback(async () => {
        try {
            const data = await organizationPositionsService.getStats();
            setStats(data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, []);

    const togglePostingHistory = async (positionId: number) => {
        if (expandedPositionId === positionId) {
            setExpandedPositionId(null);
            setPostingHistory([]);
            return;
        }
        setExpandedPositionId(positionId);
        setPostingHistoryLoading(true);
        try {
            const data = await postingService.getByPosition(positionId);
            setPostingHistory(data);
        } catch (err) {
            console.error('Failed to load posting history:', err);
            setPostingHistory([]);
        } finally {
            setPostingHistoryLoading(false);
        }
    };

    const fetchPositions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await organizationPositionsService.getPositions(1, 500);
            setAllPositions(response.data);
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to load positions. Please try again.' });
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const filteredPositions = useMemo(() => {
        let data = allPositions;
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            data = data.filter(item =>
                (item.name || '').toLowerCase().includes(lowerTerm) ||
                (item.code || '').toLowerCase().includes(lowerTerm)
            );
        }
        const activeVal = statusFilter === 'active' ? 1 : 0;
        data = data.filter(item => item.is_active === activeVal);
        if (authorityFilter !== 'all') {
            data = data.filter(item => String(item.authority_id) === authorityFilter);
        }
        if (locationFilter !== 'all') {
            data = data.filter(item => String(item.location_id) === locationFilter);
        }
        return data;
    }, [allPositions, searchTerm, statusFilter, authorityFilter, locationFilter]);

    const totalItems = filteredPositions.length;
    const paginatedPositions = useMemo(
        () => filteredPositions.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [filteredPositions, currentPage, pageSize]
    );

    useEffect(() => {
        // Wait for the permission check, and skip all loads if the user can't view —
        // otherwise the positions fetch 403s and fires a misleading error toast on top
        // of the "Not authorised" gate.
        if (viewPerm.loading || !canView) return;
        fetchStats();
        fetchPositions();
        (async () => {
            try {
                // Approved + active authorities only — getAuthorities() returns all
                // workflow states and leaked draft/unapproved authorities into the picker.
                const authRes = await authorityApi.getAuthoritiesDropdown();
                setAuthorities(authRes.items || []);
            } catch (err) {
                console.error('Failed to load authorities:', err);
            }

            try {
                // Approved + active locations only — getAll() returned all states.
                const locs = await organizationLocationsService.getDropdown();
                setLocations(locs || []);
            } catch (err) {
                console.error('Failed to load locations:', err);
            }

            try {
                // Canonical users dropdown — server enforces active + approved
                // (is_active = 1 AND is_approved = 1), entity-scoped. Never use the
                // paginated /usr/users list here: it returns draft/unapproved users too.
                const usrRes = await apiClient.get<any>('/api/v1/usr/users/dropdown/all');
                const usrData = usrRes.data?.items || usrRes.data || [];
                setUserOptions(
                    (Array.isArray(usrData) ? usrData : []).map((u: any) => ({
                        id: u.id,
                        name: u.name || `User #${u.id}`,
                        email: u.email || ''
                    }))
                );
            } catch (err) {
                console.error('Failed to load users:', err);
            }
        })();
    }, [fetchStats, fetchPositions, viewPerm.loading, canView]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'authority_id' || name === 'location_id') {
            const num = value === '' ? undefined : Number(value);
            setFormData(prev => ({ ...prev, [name]: num } as any));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreate = () => {
        setCurrentPosition(null);
        setFormData({ name: '', code: '', description: '', is_active: 1, authority_id: undefined, location_id: undefined });
        setModalError(null);
        setSubmitting(false);
        setToggling(false);
        setIsViewMode(false);
        positionVh.reset();
        setIsModalOpen(true);
    };

    const handleEdit = (position: Position) => {
        setCurrentPosition(position);
        setFormData({
            name: position.name,
            code: position.code,
            description: position.description || '',
            is_active: position.is_active,
            authority_id: (position as any).authority_id,
            location_id: (position as any).location_id
        });
        setSubmitting(false);
        setToggling(false);
        setIsViewMode(true);   // view-first: open read-only; footer "Edit" enables the form (unless approved)
        positionVh.reset();
        setIsModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentPosition) return;
        const next = currentPosition.is_active ? 0 : 1;
        const confirmFn = currentPosition.is_active ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this position?' : 'Inactivate this position?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await organizationPositionsService.updatePosition(currentPosition.id, { is_active: next } as any);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Position Reactivated.' : 'Position Inactivated.' });
            setIsModalOpen(false);
            fetchPositions();
            fetchStats();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to update status.' });
        } finally {
            setToggling(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const engineOk = positionVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        let domainOk = true;
        const trimmedName = (formData.name || '').trim();
        if (trimmedName && !isUniqueLocal(905, trimmedName, allPositions, currentPosition?.id)) {
            positionVh.setError('name', 'A position with this name already exists in this organisation.');
            domainOk = false;
        }
        if (!formData.authority_id || !formData.location_id) {
            setModalError('Please select both Authority and Location before saving.');
            return;
        }
        if (!engineOk || !domainOk) return;
        setSubmitting(true);
        try {
            if (currentPosition) {
                await organizationPositionsService.updatePosition(currentPosition.id, formData);
                setToast({ type: 'success', message: 'Position updated successfully.' });
            } else {
                await organizationPositionsService.createPosition(formData);
                setToast({ type: 'success', message: 'Position created successfully.' });
            }
            setIsModalOpen(false);
            setModalError(null);
            fetchPositions();
            fetchStats();
        } catch (err: any) {
            setModalError(err?.message || 'Failed to save position');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const fetchPositionHistory = async (positionId: number): Promise<AuditRecord[]> => {
        const p = allPositions.find(pp => Number(pp.id) === Number(positionId));
        if (!p) return [];
        const records: AuditRecord[] = [];
        if ((p as any).created_on) {
            records.push({
                audit_id: positionId * 10 + 1,
                action: 'CREATE',
                changed_by_username: (p as any).created_by_username || (p as any).created_by_name || 'System',
                changed_on: (p as any).created_on,
                old_snapshot: null,
                new_snapshot: {
                    name: p.name,
                    code: (p as any).code,
                    authority: (p as any).authority_name || (p as any).authority_id,
                    location: (p as any).location_name || (p as any).location_id,
                },
                changed_fields: ['authority_id', 'location_id', 'name'],
            });
        }
        if ((p as any).modified_on && (p as any).modified_on !== (p as any).created_on) {
            records.push({
                audit_id: positionId * 10 + 2,
                action: 'UPDATE',
                changed_by_username: (p as any).modified_by_username || (p as any).modified_by_name || 'System',
                changed_on: (p as any).modified_on,
                old_snapshot: null,
                new_snapshot: { name: p.name, code: (p as any).code },
                changed_fields: ['name'],
            });
        }
        return records;
    };

    const handleFillPosition = async (position: Position) => {
        setFillPosition(position);
        setFillFormData({
            position_id: position.id, user_id: 0,
            start_date: new Date().toISOString().split('T')[0],
            end_date: null, reference_text: '', posting_type: 'Direct'
        });
        setFillError(null);
        fillVh.reset();
        setFillModalOpen(true);
        setFillPostingsLoading(true);
        try {
            const data = await postingService.getByPosition(position.id);
            setFillPostings(data);
        } catch (err) {
            console.error('Failed to load postings for position:', err);
            setFillPostings([]);
        } finally {
            setFillPostingsLoading(false);
        }
    };

    const handleFillSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((fillPosition as any)?.workflow_state === 'approved') {
            setFillError('Record is approved and locked. Reopen via the workflow button to change the occupant.');
            return;
        }
        const fillOk = fillVh.validateAll([
            { name: 'fill_start_date', code: 305, value: fillFormData.start_date, required: true },
            { name: 'fill_reference', code: 110, value: fillFormData.reference_text },
        ]);
        if (!fillFormData.user_id) {
            setFillError('Please select a user.');
            return;
        }
        if (!fillOk) return;
        setFillSaving(true);
        setFillError(null);
        try {
            await postingService.direct({
                position_id: fillFormData.position_id,
                user_id: fillFormData.user_id,
                start_date: fillFormData.start_date,
                reference_text: fillFormData.reference_text || undefined
            });
            const data = await postingService.getByPosition(fillFormData.position_id);
            setFillPostings(data);
            setFillFormData(prev => ({
                ...prev, user_id: 0, reference_text: '',
                start_date: new Date().toISOString().split('T')[0],
                end_date: null, posting_type: 'Direct'
            }));
            fetchPositions();
        } catch (err: any) {
            const detail = err.response?.data?.detail || err.message || 'Failed to create posting';
            setFillError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        } finally {
            setFillSaving(false);
        }
    };

    const activeItem = SECTIONS.find(s => s.key === activeSection)!;

    const headerText = currentPosition
        ? isApproved ? <>Viewing (locked) "<span className="item-highlight">{currentPosition.name}</span>"</>
        : (canModify && isViewMode) ? <>Viewing "<span className="item-highlight">{currentPosition.name}</span>"</>
        : <>Editing "<span className="item-highlight">{currentPosition.name}</span>"</>
        : 'Create Position';

    return (
        <div className="flex h-full overflow-hidden">
            {/* Single card: toolbar (with view-as selector) + content. The
                previous left-rail navigation was actually a view-mode
                toggle, not a real left menu — promoted to a top-rail
                segmented control. */}
            <div className="netbody-card with-tabs flex flex-col flex-1 min-w-0">

                {/* View-as tab navigation */}
                {(viewPerm.loading || canView) && (
                    <div className="app-modal-tabs">
                        {SECTIONS.map(section => (
                            <button
                                key={section.key}
                                role="tab"
                                aria-selected={activeSection === section.key}
                                onClick={() => setActiveSection(section.key)}
                                className={`app-modal-tab${activeSection === section.key ? ' app-modal-tab--active' : ''}`}
                                data-testid={`TAB_org_positions_${section.key}`}
                            >
                                View as {section.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Toolbar */}
                {(viewPerm.loading || canView) && (
                    <div className="netbody-header">
                        {activeSection === 'list' && (
                            <>
                                {/* Status — iOS-style on/off switch (on = active) */}
                                <StatusSwitch
                                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                                    noun="positions"
                                    testId="SEL_org_positions_status_filter"
                                />
                                <div className="flex items-center gap-2 ml-auto">
                                    <FormControl.SearchSelect
                                        value={locationFilter}
                                        onChange={v => setLocationFilter(String(v ?? 'all'))}
                                        className="filter-status"
                                        data-testid="SEL_org_positions_location_filter"
                                        noSearchIcon
                                        options={[
                                            { value: 'all', label: 'All Locations' },
                                            ...locations.map(l => ({ value: String(l.id), label: l.name })),
                                        ]}
                                    />
                                    <FormControl.SearchSelect
                                        value={authorityFilter}
                                        onChange={v => setAuthorityFilter(String(v ?? 'all'))}
                                        className="filter-status"
                                        data-testid="SEL_org_positions_authority_filter"
                                        noSearchIcon
                                        options={[
                                            { value: 'all', label: 'All Authorities' },
                                            ...authorities.map(a => ({ value: String(a.id), label: a.name })),
                                        ]}
                                    />
                                    <FormControl.Input
                                        type="text"
                                        placeholder="Search positions…"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        leadingIcon={<Search size={14} />}
                                        className="filter-search"
                                        data-testid="INP_org_positions_search"
                                    />
                                    {canModify && <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_org_positions_create">Create</Button>}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className="netbody-content">

                    <div className="scroll-container flex-1 overflow-hidden">

                        {/* ── List section ──────────────────────────────────────── */}
                        {activeSection === 'list' && (
                            !viewPerm.loading && !canView ? (
                                <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <NoDataFound title="Not authorised" message="You do not have permission to view Organogram positions." />
                                </div>
                            ) : (
                                <div className="table-list">
                                    <div className="table-list-scroll">
                                        <table className="table-list-table">
                                            <thead>
                                                <tr>
                                                    <th className="table-list-th table-list-cell--dot"></th>
                                                    <th className="table-list-th table-list-cell--sno">S.No</th>
                                                    <th className="table-list-th">Code</th>
                                                    <th className="table-list-th">Position Name</th>
                                                    <th className="table-list-th">Authority</th>
                                                    <th className="table-list-th">Geography</th>
                                                    <th className="table-list-th">Current Occupant</th>
                                                    <th className="table-list-th table-list-th--right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody aria-busy={loading} aria-live="polite">
                                                {loading && <Skeleton.TableRows cols={[{ width: 'icon' }, '3xs', '2xs', 'sm', 'sm', 'sm', 'md', { width: 'icon', align: 'right' }]} />}
                                                {!loading && paginatedPositions.length === 0 && (
                                                    <tr>
                                                        <td colSpan={8} className="table-list-cell" style={{ padding: '40px 20px', textAlign: 'center' }}>
                                                            <NoDataFound
                                                                title="No positions found"
                                                                message='Click "Create" to add one.'
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                                {!loading && paginatedPositions.map((position, idx) => (
                                                    <React.Fragment key={position.id}>
                                                            <tr
                                                                className={`table-list-row${position.is_active === 1 ? '' : ' is-inactive'}`}
                                                                onClick={() => handleEdit(position)}
                                                                data-testid={`ROW_org_positions_${position.id}`}
                                                            >
                                                                <td className="table-list-cell table-list-cell--dot" title="Workflow — view timeline & act" data-testid={`BTN_org_positions_row_${position.id}_workflow`} onClick={e => { e.stopPropagation(); setWorkflowOpenFor(position); }}>
                                                                    <ApprovalDot state={(position as any).workflow_state || 'draft'} onClick={() => setWorkflowOpenFor(position)} title="Workflow — view timeline & act" />
                                                                </td>
                                                                <td className="table-list-cell table-list-cell--sno">{(currentPage - 1) * pageSize + idx + 1}</td>
                                                                <td className="table-list-cell">
                                                                    <span className="cell-code" data-testid={`CELL_org_positions_code_${position.id}`}>
                                                                        {position.code ? position.code : '—'}
                                                                    </span>
                                                                </td>
                                                                <td className="table-list-cell">
                                                                    <a href="#" onClick={(e) => e.preventDefault()} className="table-list-cell-title">
                                                                        <span className="cell-name-lock">
                                                                            {(position as any).workflow_state === 'approved' && (
                                                                                <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                                            )}
                                                                            {(position as any).name || '—'}
                                                                        </span>
                                                                    </a>
                                                                </td>
                                                                <td className="table-list-cell">{(position as any).authority_name || (position as any).authority_id || '-'}</td>
                                                                <td className="table-list-cell">{(position as any).location_name || (position as any).location_id || '-'}</td>
                                                                <td className="table-list-cell">
                                                                    {(position as any).current_occupant
                                                                        ? <span className="occupant">{(position as any).current_occupant}</span>
                                                                        : <span className="vacant">VACANT</span>
                                                                    }
                                                                </td>
                                                                <td className="table-list-cell table-list-cell--actions">
                                                                    <button
                                                                        className="table-list-action"
                                                                        title="Posting History"
                                                                        data-testid={`BTN_org_positions_row_${position.id}_history`}
                                                                        onClick={(e) => { e.stopPropagation(); togglePostingHistory(position.id); }}
                                                                    >
                                                                        {expandedPositionId === position.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                                    </button>
                                                                    <button
                                                                        className="table-list-action"
                                                                        title={!canModify ? NOT_AUTHORISED_MSG : 'Fill / Change Incumbent'}
                                                                        disabled={permLoading || !canModify}
                                                                        data-testid={`BTN_org_positions_row_${position.id}_fill`}
                                                                        onClick={(e) => { e.stopPropagation(); handleFillPosition(position); }}
                                                                    >
                                                                        <UserPlus size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="table-list-action"
                                                                        title="Audit History"
                                                                        data-testid={`BTN_org_positions_row_${position.id}_audit`}
                                                                        onClick={(e) => { e.stopPropagation(); setHistoryEntityId(Number(position.id)); setShowHistoryModal(true); }}
                                                                    >
                                                                        <History size={14} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            {expandedPositionId === position.id && (
                                                                <tr>
                                                                    <td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                                                                        <div style={{ padding: '0.75rem 1.5rem' }}>
                                                                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                                                Posting History
                                                                            </div>
                                                                            {postingHistoryLoading ? (
                                                                                <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>Loading...</div>
                                                                            ) : postingHistory.length === 0 ? (
                                                                                <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>No postings found for this position.</div>
                                                                            ) : (
                                                                                <table className="table-list-table">
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th className="table-list-th">User</th>
                                                                                            <th className="table-list-th">Start Date</th>
                                                                                            <th className="table-list-th">End Date</th>
                                                                                            <th className="table-list-th table-list-cell--status">Status</th>
                                                                                            <th className="table-list-th">Reference</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {postingHistory.map(ph => (
                                                                                            <tr key={ph.id} className="table-list-row">
                                                                                                <td className="table-list-cell">{ph.user_name || '-'}</td>
                                                                                                <td className="table-list-cell">{formatDate(ph.start_date)}</td>
                                                                                                <td className="table-list-cell">{formatDate(ph.end_date)}</td>
                                                                                                <td className="table-list-cell table-list-cell--status">
                                                                                                    <span className={activeBadgeCss(!ph.end_date ? 1 : 0)}>
                                                                                                        {!ph.end_date ? 'Active' : 'Ended'}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td className="table-list-cell">{ph.reference_text || '-'}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                    </table>
                                </div>
                                <Pagination
                                    page={currentPage}
                                    pageSize={pageSize}
                                    totalRecords={totalItems}
                                    onPageChange={setCurrentPage}
                                    onPageSizeChange={setPageSize}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    recordLabel="positions"
                                />
                                </div>
                            )
                        )}

                        {/* ── Authority section ─────────────────────────────────── */}
                        {activeSection === 'authority' && (
                            <div className="authority-list">
                                {authorities.map(a => {
                                    const count = allPositions.filter(p => String((p as any).authority_id) === String(a.id)).length;
                                    return (
                                        <div key={a.id} className="authority-card">
                                            <div className="authority-name">{a.name}</div>
                                            <div className="authority-count">{count} positions</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ── Geography section ─────────────────────────────────── */}
                        {activeSection === 'geography' && (
                            <div className="geography-list">
                                {locations.map(l => {
                                    const count = allPositions.filter(p => String((p as any).location_id) === String(l.id)).length;
                                    return (
                                        <div key={l.id} className="geo-card">
                                            <div className="geo-name">{l.name}</div>
                                            <div className="geo-count">{count} positions</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* ── Position modal ──────────────────────────────────────────── */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="md" data-testid="MOD_org_position_form">
                <Modal.Header>{headerText}</Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <form id="position-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
                        {modalError && <div className="form-error">{modalError}</div>}
                        <ValidatedInput
                            vh={positionVh}
                            name="name"
                            code={109}
                            required
                            label="Position Name"
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            disabled={fieldsDisabled}
                            placeholder="e.g. Senior Developer"
                            data-testid="INP_org_position_name"
                        />
                        <FormControl.SearchSelect
                            label="Authority"
                            required
                            name="authority_id"
                            value={formData.authority_id ?? null}
                            onChange={(value) => setFormData(prev => ({ ...prev, authority_id: value ? Number(value) : undefined } as any))}
                            disabled={fieldsDisabled}
                            placeholder="Select Authority..."
                            clearable
                            options={authorities.map((a: any) => ({ value: a.id, label: a.code ? `${a.name} (${a.code})` : a.name }))}
                            data-testid="SEL_org_position_authority"
                        />
                        <FormControl.SearchSelect
                            label="Location"
                            required
                            name="location_id"
                            value={formData.location_id ?? null}
                            onChange={(value) => setFormData(prev => ({ ...prev, location_id: value ? Number(value) : undefined } as any))}
                            disabled={fieldsDisabled}
                            placeholder="Select Location..."
                            clearable
                            options={locations.map((l) => ({ value: l.id, label: l.name }))}
                            data-testid="SEL_org_position_location"
                        />
                        <FormControl.Input
                            label="Code"
                            type="text"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            disabled
                            placeholder={currentPosition ? '' : 'Auto-generated on save'}
                            data-testid="INP_org_position_code"
                        />
                        <ValidatedTextarea
                            vh={positionVh}
                            name="description"
                            code={110}
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            disabled={fieldsDisabled}
                            rows={3}
                            placeholder="Enter position description..."
                            data-testid="INP_org_position_description"
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={currentPosition ? 'app-modal-footer--split' : ''}>
                    {/* Active/Inactive toggle — existing record with modify rights (viewing + editing), but not if approved */}
                    {currentPosition && canModify && !isApproved && (
                        <Button
                            variant={currentPosition.is_active ? 'inactive-outline' : 'active-outline'}
                            icon={currentPosition.is_active ? CircleMinus : CircleCheck}
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                            data-testid="BTN_org_position_toggle_active"
                        >
                            {toggling ? '...' : currentPosition.is_active ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    {/* View → Edit: existing record, read-only (brand color), but not if approved */}
                    {currentPosition && isViewMode && canModify && !isApproved && (
                        <Button
                            variant="fill"
                            icon={Edit2}
                            onClick={() => setIsViewMode(false)}
                            disabled={permLoading || !canModify}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_org_position_edit"
                        >
                            Edit
                        </Button>
                    )}
                    {/* Submit (create) or Update (editing existing), but not if approved */}
                    {!isViewMode && currentPosition?.workflow_state !== 'approved' && (
                        <Button
                            type="submit"
                            form="position-form"
                            variant={currentPosition ? 'update' : 'fill'}
                            icon={currentPosition ? Check : Save}
                            disabled={permLoading || !canModify || submitting || toggling}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_org_position_submit"
                        >
                            {submitting
                                ? (currentPosition ? 'Updating...' : 'Submitting...')
                                : (currentPosition ? 'Update' : 'Submit')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={() => setWorkflowOpenFor(null)}
                kind="org_position"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={fetchPositions}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />

            {/* ── Audit History modal ─────────────────────────────────────── */}
            {showHistoryModal && historyEntityId && (
                <AuditHistoryModal
                    show={showHistoryModal}
                    onClose={() => { setShowHistoryModal(false); setHistoryEntityId(null); }}
                    entityId={historyEntityId}
                    entityType="positions"
                    entityName={allPositions.find(p => Number(p.id) === Number(historyEntityId))?.name}
                    fetchHistoryFn={fetchPositionHistory}
                />
            )}

            {/* ── Fill Position modal ─────────────────────────────────────── */}
            <Modal isOpen={fillModalOpen && !!fillPosition} onClose={() => setFillModalOpen(false)} size="lg" data-testid="MOD_org_fill_position">
                <Modal.Header>
                    {fillPosition ? (
                        <>
                            Fill Position — {fillPosition.name}
                            <span style={{ color: '#94a3b8', fontSize: '0.8125rem', fontWeight: 400, marginLeft: 8 }}>({fillPosition.code})</span>
                        </>
                    ) : 'Fill Position'}
                </Modal.Header>
                <Modal.Body>
                    <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        {fillLocked && (
                            <div className="approved-banner">
                                <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to change the occupant.
                            </div>
                        )}
                        <div >
                            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0369a1', marginBottom: '0.75rem' }}>Assign New Incumbent</div>
                            {fillError && <div className="form-error">{fillError}</div>}
                            <form
                                id="fill-position-form"
                                onSubmit={handleFillSubmit}
                                className="grid grid-cols-2 gap-x-6 gap-y-4"
                            >
                                <FormControl.SearchSelect
                                    label="User"
                                    required
                                    value={fillFormData.user_id || null}
                                    onChange={(value) => setFillFormData(prev => ({ ...prev, user_id: value ? Number(value) : 0 }))}
                                    disabled={fillLocked}
                                    placeholder="Select User..."
                                    clearable
                                    options={userOptions.map(u => ({ value: u.id, label: u.name }))}
                                    data-testid="SEL_org_fill_user"
                                />
                                <FormControl.SearchSelect
                                    label="Posting Type"
                                    value={fillFormData.posting_type || 'Direct'}
                                    onChange={(value) => setFillFormData(prev => ({ ...prev, posting_type: String(value) }))}
                                    disabled={fillLocked}
                                    options={POSTING_TYPES.map(t => ({ value: t, label: t }))}
                                    data-testid="SEL_org_fill_posting_type"
                                />
                                <ValidatedDatePicker
                                    vh={fillVh}
                                    name="fill_start_date"
                                    code={305}
                                    required
                                    label="Start Date"
                                    value={fillFormData.start_date}
                                    onChange={(e: any) => setFillFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                    disabled={fillLocked}
                                    data-testid="INP_org_fill_start_date"
                                />
                                <ValidatedInput
                                    vh={fillVh}
                                    name="fill_reference"
                                    code={110}
                                    label="Reference / Order No."
                                    type="text"
                                    value={fillFormData.reference_text || ''}
                                    onChange={(e) => setFillFormData(prev => ({ ...prev, reference_text: e.target.value }))}
                                    disabled={fillLocked}
                                    placeholder="e.g. HR/2026/001"
                                    data-testid="INP_org_fill_reference"
                                />
                            </form>
                        </div>

                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Posting History
                        </div>
                        {fillPostingsLoading ? (
                            <div style={{ fontSize: '0.8125rem', color: '#94a3b8', padding: '1rem 0' }}>Loading...</div>
                        ) : fillPostings.length === 0 ? (
                            <div style={{ fontSize: '0.8125rem', color: '#94a3b8', padding: '1rem 0' }}>No postings yet for this position.</div>
                        ) : (
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th">User</th>
                                        <th className="table-list-th">Type</th>
                                        <th className="table-list-th">From</th>
                                        <th className="table-list-th">To</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                        <th className="table-list-th">Ref</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fillPostings.map(ph => (
                                        <tr key={ph.id} className="table-list-row">
                                            <td className="table-list-cell">{ph.user_name || '-'}</td>
                                            <td className="table-list-cell">{ph.posting_type || 'Direct'}</td>
                                            <td className="table-list-cell">{formatDate(ph.start_date)}</td>
                                            <td className="table-list-cell">{formatDate(ph.end_date)}</td>
                                            <td className="table-list-cell table-list-cell--status">
                                                <span className={activeBadgeCss(!ph.end_date ? 1 : 0)}>
                                                    {!ph.end_date ? 'Current' : 'Ended'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">{ph.reference_text || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="submit"
                        form="fill-position-form"
                        variant="fill"
                        icon={Save}
                        disabled={permLoading || !canModify || fillSaving || fillLocked}
                        title={!canModify
                            ? NOT_AUTHORISED_MSG
                            : fillLocked
                                ? 'Record is approved and locked. Reopen via the workflow button to change the occupant.'
                                : undefined}
                        data-testid="BTN_org_fill_submit"
                    >
                        {fillSaving ? 'Submitting...' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default OrganizationPositions;
